import { getPlatform } from '~/utils/platform-utils';
import { parseTicketCommentBy } from '~/utils/comment-parser';
import LIST_CATEGORY from '~/static/data/listCategory';
import {
  retrieveTickets,
  retrieveTicketDetail,
  retrieveTicketComments,
  updateTicket,
  uploadTicketAttachment
} from '~/api/mitra-bantuan';
import { fetchNeoToggles } from '~/api/neo';

const getWebApiHost = () => process.env.MITRA_HOST;

export const state = () => ({
  tickets: [],
  currTicket: {},
  requesterId: 0,
  comments: [],
  customFieldsId: '360028579734',
  listCategory: LIST_CATEGORY,
  commentsStatus: 'preparing',
});

export const getters = {
  getTickets(state) {
    return state.tickets || [];
  },
  getCurrentTicket(state) {
    return state.currTicket;
  },
  getRequesterId(state) {
    return state.requesterId;
  },
  getTicketComments(state) {
    const userRegex = (state.currTicket?.subject ?? '').match(
      /(?:Missed )?(?:Chat|Conversation|conversation) with (.+)/
    );
    const userName = userRegex && userRegex.length > 1 ? userRegex[1] : '';
    let parsedChats = [];
    let isChatGpt = false;

    (state.comments || []).forEach(comment => {
      if (!comment.public || !comment.via) return;
      isChatGpt = comment.via.channel === 'chat_gpt';
      parsedChats = parsedChats.concat(parseTicketCommentBy[comment.via.channel]({ comment, userName }));
    });

    // if the ticket from GPT, all the comment.via.channel is chat_gpt
    // the chat data ordering when retrieve a comment from gpt is order by created at descending,
    // so we need to reverse it to make the data order by ascending
    if (isChatGpt) {
      parsedChats.reverse();
    }

    return parsedChats.map((chat, idx) => ({ id: idx + 1, ...chat })) || [];
  },
  getListCategory(state) {
    return state.listCategory;
  },
  getCustomFieldsId(state) {
    return state.customFieldsId;
  },
  getCommentsStatus(state) {
    return state.commentsStatus;
  },
  getClosedTickets(state) {
    return (state.tickets || []).filter(ticket => {
      const hasTriggeredAndNotConnectedWithCSTicket = ticket.tags?.includes('o2o_chat_triggered_connect_with_cs') && !ticket.tags?.includes('o2o_chat_connected_with_cs');
      const eligibleStatusTicket = ['solved', 'closed'].includes(ticket.status);
      return hasTriggeredAndNotConnectedWithCSTicket || eligibleStatusTicket;
    });
  },
  getActiveTickets(state) {
    return (state.tickets || []).filter(ticket => {
      const activeTicket = ['new', 'open', 'pending', 'hold'].includes(ticket.status);
      const nonHasTriggeredAndNotConnectedWithCSTicket = !(ticket.tags?.includes('o2o_chat_triggered_connect_with_cs') && !ticket.tags?.includes('o2o_chat_connected_with_cs'));
      const nonActiveChatSessionTicket = ticket.tags?.includes('zopim_chat_ended');
      const nonChatbotTicket = !ticket.isChatbotTicket && ticket.comment !== '';
      return activeTicket && nonHasTriggeredAndNotConnectedWithCSTicket && nonActiveChatSessionTicket && nonChatbotTicket;
    });
  },
  hasClosedTickets(_state, getters) {
    return (getters.getClosedTickets || []).length > 0;
  },
  hasActiveTickets(_state, getters) {
    return (getters.getActiveTickets || []).length > 0;
  },
};

export const mutations = {
  setTickets(state, tickets) {
    state.tickets = tickets || [];
  },
  updateTickets(state, tickets) {
    const newTickets = state.tickets || [];
    state.tickets = newTickets.map(ticket => {
      const newTicket = (tickets || []).find(t => t.id === ticket.id);
      return newTicket ?? ticket;
    });
  },
  setCurrentTicket(state, ticket) {
    state.currTicket = ticket || {};
    state.requesterId = ticket?.requester_id || 0;
  },
  setRequesterId(state, id) {
    state.requesterId = id || 0;
  },
  setTicketComments(state, comments) {
    state.comments = comments || [];
  },
  setNewComment(state, comment) {
    state.comments = [...(state.comments || []), { ...comment }];
  },
  emptyTicket(state) {
    state.currTicket = {};
    state.comments = [];
    state.requesterId = 0;
  },
  setCommentsStatus(state, val) {
    state.commentsStatus = val || 'preparing';
  },
};

export const actions = {
  async fetchTickets({ commit, dispatch }, params) {
    try {
      const { data = [] } = await fetchNeoToggles(['o2o/migration-zendesk-api-enabled']);
      const isActive = data.find(element => element.id === 'o2o/migration-zendesk-api-enabled')?.active || false;
      let endResult;
      if (isActive) {
        const response = await retrieveTickets(this.$axios, params);
        endResult = response.data;
      } else {
        const response = await this.$axios.$post(`${getWebApiHost()}/zopim/get_tickets`, params);
        endResult = response.results;
      }

      // Exclude any ticket which `tags` includes all the tags in MARKETPLACE_TICKET_TAGS
      endResult = endResult?.filter(result => result.tags?.includes('skill_mitra')) || [];
      commit('setTickets', endResult);
      return endResult;
    } catch (e) {
      console.error('[ERROR FETCH TICKETS]', e);
      dispatch(
        'flashalert/showFlashAlert',
        { message: 'Gagal mendapatkan data. Sedang dalam perbaikan.', type: 'warning' },
        { root: true }
      );
      // Ensure we always set a valid state even on error
      commit('setTickets', []);
      return [];
    }
  },
  async fetchTicketById({ commit, dispatch }, payload) {
    let resp;
    try {
      const { data = [] } = await fetchNeoToggles(['o2o/migration-zendesk-api-enabled']);
      const isActive = data.find(element => element.id === 'o2o/migration-zendesk-api-enabled')?.active || false;
      if (isActive) {
        const result = await retrieveTicketDetail(this.$axios, {
          id: payload
        });
        resp = {
          'ticket': result.data,
        };
      } else {
        resp = await this.$axios.$post(`${getWebApiHost()}/zopim/get_ticket_by_id`, {
          id: parseInt(payload, 10),
        });
      }
    } catch (e) {
      dispatch(
        'flashalert/showFlashAlert',
        { message: 'Gagal mendapatkan data. Sedang dalam perbaikan.', type: 'warning' },
        { root: true }
      );
      return;
    }
    commit('setCurrentTicket', resp.ticket);
    return resp.ticket;
  },
  async fetchTicketComments({ commit, dispatch }, { id, shouldCommit = true }) {
    let resp;
    try {
      const { data = [] } = await fetchNeoToggles(['o2o/migration-zendesk-api-enabled']);
      const isActive = data.find(element => element.id === 'o2o/migration-zendesk-api-enabled')?.active || false;
      if (isActive) {
        const result = await retrieveTicketComments(this.$axios, {
          id: parseInt(id, 10),
        });
        resp = {
          'comments': result.data
        };
      } else {
        resp = await this.$axios.$post(`${getWebApiHost()}/zopim/get_ticket_comments`, {
          id,
        });
      }
    } catch (e) {
      dispatch(
        'flashalert/showFlashAlert',
        { message: 'Gagal mendapatkan data. Sedang dalam perbaikan.', type: 'warning' },
        { root: true }
      );
      commit('setCommentsStatus', 'error');
      return;
    }
    if (shouldCommit) commit('setTicketComments', resp.comments);
    if (resp.comments.length === 0) commit('setCommentsStatus', 'preparing');
    return resp.comments;
  },
  async updateTicket({ dispatch, commit }, { id, requestPayload, shouldCommit = true }) {
    try {
      if (requestPayload?.ticket?.comment?.body) {
        const { body } = requestPayload.ticket.comment;
        requestPayload.ticket.comment.body = `${body}\n\n--- Via ${getPlatform().name} ---`;
      }
      const { data = [] } = await fetchNeoToggles(['o2o/migration-zendesk-api-enabled']);
      const isActive = data.find(element => element.id === 'o2o/migration-zendesk-api-enabled')?.active || false;
      let endResult;
      if (isActive) {
        const response = await updateTicket(this.$axios, {
          id: parseInt(id, 10),
          requestPayload,
        });
        endResult = response.data;
      } else {
        const response = await this.$axios.$post(`${getWebApiHost()}/zopim/update_ticket`, {
          id: parseInt(id, 10),
          requestPayload,
        });
        endResult = response.ticket;
      }

      if (shouldCommit) commit('setCurrentTicket', endResult);
    } catch (e) {
      dispatch(
        'flashalert/showFlashAlert',
        { message: 'Gagal mengirim data. Sedang dalam perbaikan.', type: 'warning' },
        { root: true }
      );
    }
  },
  async uploadFiles({ dispatch }, file) {
    try {
      const { data = [] } = await fetchNeoToggles(['o2o/migration-zendesk-api-enabled']);
      const isActive = data.find(element => element.id === 'o2o/migration-zendesk-api-enabled')?.active || false;
      if (isActive) {
        const fileBase64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result.split(',')[1]); // Remove the data:image/png;base64, part
          reader.onerror = error => reject(error);
          reader.readAsDataURL(file);
        });
        const response = await uploadTicketAttachment(this.$axios, {
          fileName: file.name,
          fileBase64
        });
        return { upload: response.data };
      }

      const bodyFormData = new FormData();
      bodyFormData.append('upload', file);
      const resp = await this.$axios.$post(`${getWebApiHost()}/zopim/upload_files?filename=${file.name}`, bodyFormData);
      return resp;
    } catch (e) {
      dispatch(
        'flashalert/showFlashAlert',
        { message: 'Gagal mengirim data. Sedang dalam perbaikan.', type: 'warning' },
        { root: true }
      );
    }
  },
  addNewComment({ commit }, comment) {
    commit('setNewComment', comment);
  },
  emptyTicket({ commit }) {
    commit('emptyTicket');
  },
  updateTickets({ commit }, tickets) {
    commit('updateTickets', tickets);
  },
};
